import { Link } from '@tanstack/react-router'
import { useAuth } from '../../contexts/AuthContext'
import { useInStanding } from '../../hooks/useInStanding'
import { CLUB_AREAS } from './clubAreas'
import { Container } from './Container'
import { PRODUCT_LONG_NAME, PRODUCT_NAME } from '../../lib/product'

interface FootLink {
  to: string
  label: string
}

interface FootColumn {
  heading: string
  links: readonly FootLink[]
}

/**
 * The Club column is `CLUB_AREAS` — the member nav's own list, so the foot
 * can never name a different clubhouse than the nav does.
 *
 * The Membership column follows the session twice. Its last link: offering
 * "Sign in" to someone already signed in is the sort of small wrongness that
 * makes a site feel unattended. Its first: "Terms & tiers" is payment
 * material, and is withheld from a member in standing — the terms are the next
 * step for a visitor or a lapsed member, and a second bill for someone who has
 * paid (decision record §12.2). The charter stays for everyone.
 */
function columns(isAuthenticated: boolean, inStanding: boolean): FootColumn[] {
  return [
    {
      heading: 'The Club',
      links: CLUB_AREAS,
    },
    {
      heading: 'Membership',
      links: [
        ...(inStanding ? [] : [{ to: '/membership', label: 'Terms & tiers' }]),
        { to: '/the-club', label: 'The charter' },
        isAuthenticated
          ? { to: '/clubhouse/account', label: 'Your account' }
          : { to: '/sign-in', label: 'Sign in' },
      ],
    },
  ]
}

const linkClass = 'block rounded-club-sm text-[13.5px] text-ink transition-colors hover:text-fairway'

/**
 * The club's foot. `/privacy` is a Rails-rendered page, not a SPA route, so it
 * stays a plain anchor — a router Link would try to resolve it client-side and
 * dead-end.
 */
export function ClubFooter() {
  const { isAuthenticated } = useAuth()
  const inStanding = useInStanding()

  return (
    <footer className="bg-paper pb-10 pt-16">
      <Container>
        <div className="grid gap-9 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="font-display text-lg font-semibold text-pine">{PRODUCT_NAME}</div>
            <p className="mt-3 max-w-[32ch] text-[13.5px] text-ink-soft">
              {PRODUCT_LONG_NAME} — a private virtual club, chartered MMXXVI. Membership has its
              privileges.
            </p>
          </div>

          {columns(isAuthenticated, inStanding).map((column) => (
            <div key={column.heading}>
              <h2 className="mb-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brass-deep">
                {column.heading}
              </h2>
              {column.links.map((link) => (
                <Link key={link.to + link.label} to={link.to} className={`${linkClass} mb-2.5`}>
                  {link.label}
                </Link>
              ))}
            </div>
          ))}

          <div>
            <h2 className="mb-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brass-deep">
              The Fine Print
            </h2>
            <a href="/privacy" className={`${linkClass} mb-2.5`}>
              Privacy
            </a>
            <a href="mailto:privacy@octl.ai" className={linkClass}>
              Contact the steward
            </a>
          </div>
        </div>

        <div className="mt-11 flex flex-wrap justify-between gap-2 border-t border-brass/35 pt-5.5 text-xs text-ink-soft">
          <span>© MMXXVI {PRODUCT_LONG_NAME}</span>
          <span className="font-display italic">
            Discretion assured. Proximity to the exit, always.
          </span>
        </div>
      </Container>
    </footer>
  )
}
